// @ts-nocheck
import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { useLocation } from "react-router-dom";
// components
import CheckoutForm from "../components/CheckoutForm";
import PageHeading from '../components/PageHeading';
import BasketInformation from '../components/BasketInformation';
import Select from "../components/Select";
import Button from "../components/Button";
// @interfaces
import { IBasket } from "../@interfaces/basket.interface";
import { IQuote, IPurchase } from "../@interfaces/purchase.interface";
import { IMerchantConfig } from "../@interfaces/config.interface";
import { ICarbonOffsetAmount } from "../@interfaces/quote.interface";
// utils
import { formatPriceTextOnly } from '../utils/priceFormatter';
import { getBasketByCodeApi } from "../api/projectApi";
// styles
import styles from "../styles/pages/payment/PaymentPage.module.css";
import TransparentAndVerified from "../components/TransparentAndVerified";
import { sendAnalyticsEvent } from "../utils/analytics";
import commonStyles from "../styles/commonStyles.module.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_CLIENT_ID);

interface PaymentPageProps {
  programmeCode?: string | null;
  quote: IQuote | null;
  basket: IBasket | null;
  setPurchase: (purchase: IPurchase) => void;
  setActiveStep: (step: number) => void;
  restoreDefaultState: () => void;
  setUserEmail: (email: string) => void;
  offsetAmount: ICarbonOffsetAmount;
  disableCurrencyList: boolean;
  selectedParams: ISelectedParams;
  setSelectedParams: React.Dispatch<React.SetStateAction<ISelectedParams>>
  merchantConfig: IMerchantConfig;
  createMultipleQuoteByEstimate: (params: { basket?: IBasket, currency?: string }) => void;
}

const PaymentPage: React.FC<PaymentPageProps> = ({
  programmeCode,
  quote,
  setPurchase,
  basket,
  restoreDefaultState,
  setActiveStep,
  setUserEmail,
  offsetAmount,
  disableCurrencyList,
  selectedParams,
  setSelectedCurrency,
  merchantConfig,
  createMultipleQuoteByEstimate,
  setSelectedParams,
}) => {
  const location = useLocation();
  const search = useMemo(() => new URLSearchParams(location.search), [location]);

  const basketCodesParam = useMemo(() => search.getAll('basketCode'), [search]);

  const paymentPage = merchantConfig?.pages?.payment;

  const [showTooltip, setShowTooltip] = useState(false);
  const [baskets, setBaskets] = useState([]);
  const [basketsError, setBasketsError] = useState('');

  const getBaskets = async (codes: string[]) => {
    try {
      const result = await getBasketByCodeApi(codes);

      if (result?.data) {
        setBaskets(result.data.content);
      }
    } catch {
      setBasketsError('Something went wrong');
    }
  }

  const basketsToDisplay: IBasket[] = useMemo(() => {
    return baskets.map(b => b?.code && ({ ...b, label: b?.name }))
  }, [baskets]);

  useEffect(() => {
    setBasketsError('');

    if (basketCodesParam.length) {
      getBaskets(basketCodesParam);
    }
  }, [basketCodesParam]);

  const appearance = {
    theme: 'stripe',
  };
  const options = {
    appearance,
    fonts: [
      {
        cssSrc: `https://fonts.googleapis.com/css2?family=Titillium+Web:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;0,900;1,200;1,300;1,400;1,600`,
        family: "Titillium Web",
        style: "normal",
      },
      {
        cssSrc: `https://fonts.googleapis.com/css2?family=Public+Sans:wght@100;200;300;400;500;600;700;800;900`,
        family: "Public Sans",
        style: "normal",
      }
    ],
  };

  const onCheckoutSuccess = (purchase: IPurchase) => {
    sendAnalyticsEvent('purchase', { 
      co2Kgs: quote.carbonOffsetQuantity.value.toString() as string, 
      itemName: "Journey Offset", 
      price: quote.offsetPrice,
      transactionId: purchase.purchaseId
    });
    setPurchase(purchase);
    setActiveStep(3);
  }

  const renderCurrencyList = useMemo(() => {
    const currenciesToRender = [
      ...(merchantConfig?.locales?.currencies || [])
    ];

    if (selectedParams?.currency && !currenciesToRender.includes(selectedParams.currency)) {
      currenciesToRender.push(selectedParams.currency);
    }

    return currenciesToRender.map((currency) => ({ value: currency, label: currency }))
  }, [merchantConfig?.locales?.currencies, selectedParams.currency]);

  const getDefaultCurrencyToDisplay = (code: string, list: { value: string, label: string }[]) => {
    if (!code || !list) return;

    return list?.find((el: any) => el?.value === code);
  }

  const defaultCurrency = getDefaultCurrencyToDisplay(selectedParams.currency, renderCurrencyList);

  const handleTooltipToggle = (e) => {
    e.stopPropagation();
    setShowTooltip(!showTooltip);
  }

  const handleTooltipHide = () => {
    if(showTooltip) {
      setShowTooltip(false);
    }
  }

  return (
    <div onClick={handleTooltipHide} className={`${styles.container} delayed-fadein`}>
      <PageHeading
        title={
          !quote.offsetRatio?.overridden ?
              paymentPage?.header?.title
              : 'Great, your footprint is low'
        }
        text={
          !quote.offsetRatio?.overridden ?
              paymentPage?.header?.text
              : 'Offset a little bit more than your emissions to reduce your impact even further'
        }
      />

      <div className={styles.border}>

        <div className={styles.wrapper}>
          <div>
            <div className={styles.offsetInfo}>
              <div className={styles.offset}>
                <div>
                  <p style={{ fontSize: '0.75rem', marginBottom: '0.5rem', position: 'relative' }}>Your carbon emissions
                    {quote?.offsetRatio?.overridden &&
                      <>
                        <img
                        className={styles.offsetIncreasedInfo}
                        src={`https://res.cloudinary.com/carbonclick/image/upload/v1685958516/widgets/info_zy7tlr.svg`}
                        width={16}
                        height={16}
                        alt="Info icon"
                        onClick={handleTooltipToggle}
                        />
                        <div onClick={(e) => {e.stopPropagation();}} style={{opacity: showTooltip ? '1' : '0'}} className={styles.tooltipWrapper}>
                          <div className={styles.tooltipTriangle}/>
                          <div className={styles.tooltip}>
                            We have optimized your contribution amount to a fair minimum, guaranteeing that a substantial percentage of your contribution directly supports impactful carbon offsetting projects
                          </div>
                        </div>
                      </>}
                  </p>

                  <div>
                    <img
                      src={`https://res.cloudinary.com/carbonclick/image/upload/v1681212015/widgets/Activity%20Widget/co2_Icon_a9gfpp.svg`}
                      width={44}
                      height={27}
                      alt="Co2 icon"
                    />
                  </div>
                  <h3 className={styles.offsetTitle}>{Math.round(offsetAmount?.value)?.toLocaleString()} {offsetAmount?.unit?.toLocaleLowerCase()}</h3>
                  <p className={styles.offsetSubtitle}>Carbon to offset</p>
                </div>
              </div>

              <div className={styles.offset}>
                <div>
                  <p style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Your contribution</p>
                  <div>
                    <img
                      src={`https://res.cloudinary.com/carbonclick/image/upload/v1681902009/widgets/Journey%20Widget/planet-icon_ac1w6j.svg`}
                      width={29}
                      height={29}
                      alt="Co2 icon"
                    />
                  </div>
                  <h3 className={styles.offsetTitle}>{formatPriceTextOnly(quote?.offsetPrice)}</h3>
                  <p className={styles.offsetSubtitle}>offsets your carbon emissions</p>
                </div>
              </div>

            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <p style={{ paddingBottom: '0.8rem', textAlign: 'center', fontSize: '14px', fontWeight: 600 }}>Take climate action by funding these climate projects</p>
              {basketCodesParam.length ? (
                <>
                  {basketsError && <p className={commonStyles.errorMessage}>{basketsError}</p>}
                  <Select
                    selectedItem={selectedParams.basket || basketsToDisplay[0] || ''}
                    onChange={(v) => setSelectedParams((prev) => ({ ...prev , basket: v }))}
                    optionsList={basketsToDisplay}
                    label="Choose project portfolio"
                    onOffsetClick={createMultipleQuoteByEstimate}
                    className={styles.basketSelector}
                  />
                  <BasketInformation projects={selectedParams.basket?.projects || basketsToDisplay[0]?.projects} />
                </>
              ) : (
                <BasketInformation projects={basket?.projects} />
              )}
            </div>

            <div className={styles.hideVerifiedSection}>
              <TransparentAndVerified />
            </div>
          </div>

          <div>
            <Elements options={options} stripe={stripePromise}>
              <CheckoutForm
                programmeCode={programmeCode}
                quote={quote}
                setUserEmail={setUserEmail}
                onCheckoutSuccess={onCheckoutSuccess}
                disableCurrencyList={disableCurrencyList}
                merchantConfig={merchantConfig}
                setSelectedParams={setSelectedParams}
                selectedParams={selectedParams}
                onOffsetClick={createMultipleQuoteByEstimate}
                SelectComponent={
                  <Select
                    selectedItem={defaultCurrency}
                    onChange={(v) => setSelectedParams((prev) => ({ ...prev , currency: v.value }))}
                    optionsList={renderCurrencyList}
                    label="Currency"
                    noTranslate
                    onOffsetClick={createMultipleQuoteByEstimate}
                  />
                }
              />
            </Elements>
          </div>
        </div>

        <div className={styles.displayVerifiedSection}>
          <TransparentAndVerified />
        </div>
      </div>

      <Button
        text="Start again"
        variant="arrow"
        iconName="back"
        onClick={restoreDefaultState}
      />
    </div>
  );
};

export default PaymentPage;
